/**
 * Small, dependency-free helpers shared by the domain layer.
 * The domain layer intentionally does not access DOM, network, or storage APIs.
 */

/** @template T @param {T} value @returns {T} */
export function cloneValue(value) {
  if (typeof globalThis.structuredClone === 'function') {
    return globalThis.structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

/** @template T @param {T} value @returns {Readonly<T>} */
export function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  Object.freeze(value);
  Object.values(value).forEach(deepFreeze);
  return value;
}

/** @param {unknown} value @returns {Record<string, any>} */
export function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

/** @template T @param {T[] | T | null | undefined} value @returns {T[]} */
export function asArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : value == null ? [] : [value];
}

/** @param {unknown} value @param {string} fallback @returns {string} */
export function text(value, fallback = '') {
  const normalized = String(value ?? '').trim();
  return normalized || fallback;
}

/** @param {unknown[]} values @returns {string[]} */
export function uniqueStrings(values) {
  return [...new Set(values.map(value => text(value)).filter(Boolean))];
}

/**
 * Reorders known items and appends new/unknown source items in their original order.
 * @template T
 * @param {T[]} items
 * @param {string[]} orderedIds
 * @param {(item: T) => string} [getId]
 * @returns {T[]}
 */
export function orderByIds(items, orderedIds, getId = item => item.id) {
  const byId = new Map(items.map(item => [getId(item), item]));
  const result = [];
  for (const id of orderedIds || []) {
    if (!byId.has(id)) continue;
    result.push(byId.get(id));
    byId.delete(id);
  }
  for (const item of items) {
    const id = getId(item);
    if (byId.has(id)) {
      result.push(item);
      byId.delete(id);
    }
  }
  return result;
}

/** @param {unknown} value @returns {unknown} */
export function parseJsonObject(value) {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}
